<template>
  <v-dialog :model-value="modelValue" max-width="540" @update:model-value="$emit('update:modelValue', $event)">
    <v-card class="settings-dialog">
      <v-card-title class="settings-dialog__title">
        <span class="settings-dialog__title-text">Settings</span>
        <div class="settings-dialog__pill-toggle">
          <button
            type="button"
            class="settings-dialog__pill"
            :class="{ 'settings-dialog__pill--active': store.scope === SCOPE.PA }"
            @click="setScope(SCOPE.PA)">
            Prior Auth
          </button>
          <button
            type="button"
            class="settings-dialog__pill"
            :class="{ 'settings-dialog__pill--active': store.scope === SCOPE.INTAKE }"
            @click="setScope(SCOPE.INTAKE)">
            Intake
          </button>
          <button
            v-if="worklistShown"
            type="button"
            class="settings-dialog__pill"
            :class="{ 'settings-dialog__pill--active': store.scope === SCOPE.WORKLIST }"
            @click="setScope(SCOPE.WORKLIST)">
            Worklist
          </button>
        </div>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('update:modelValue', false)" />
      </v-card-title>

      <v-tabs v-model="topTab" color="primary" class="settings-dialog__tabs">
        <v-tab v-for="tab in COLUMN_TABS[store.scope]" :key="tab" :value="tab">{{ titleCaseString(tab) }}</v-tab>
      </v-tabs>

      <!-- Columns tab content -->
      <template v-if="topTab === 'columns'">
        <v-card-text class="settings-dialog__content">
          <div v-if="!store.canEditColumns" class="settings-dialog__loading">
            <v-progress-circular indeterminate size="18" width="2" />
            Loading your saved layout…
          </div>
          <div v-else class="settings-dialog__list">
            <div
              v-for="(item, index) in currentItems"
              v-show="!item.hidden"
              :key="item.key"
              class="settings-dialog__row"
              :class="{
                'settings-dialog__row--dragging': dragIndex === index,
                'settings-dialog__row--drop-target': dropTargetIndex === index,
                'settings-dialog__row--pinned': item.pinned,
              }"
              :draggable="!item.pinned"
              @dragstart="onDragStart($event, index)"
              @dragover.prevent="onDragOver($event, index)"
              @dragleave="onDragLeave(index)"
              @drop="onDrop($event, index)"
              @dragend="onDragEnd">
              <v-icon
                icon="mdi-drag"
                size="18"
                class="settings-dialog__drag-handle"
                :class="{ 'settings-dialog__drag-handle--disabled': item.pinned }" />
              <span class="settings-dialog__label">{{ item.label }}</span>
              <v-chip v-if="item.pinned" size="x-small" color="grey" variant="outlined" class="settings-dialog__chip">
                Required
              </v-chip>
              <v-switch
                :model-value="item.visible"
                :disabled="item.pinned"
                density="compact"
                hide-details
                color="primary"
                class="settings-dialog__switch"
                @update:model-value="onToggle(item.key, $event)" />
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="settings-dialog__actions">
          <v-btn variant="text" size="small" :disabled="!store.canEditColumns" @click="onReset"
            >Reset to Defaults</v-btn
          >
          <v-spacer />
          <span v-if="store.saveError" class="settings-dialog__save-error">
            <v-icon icon="mdi-cloud-off-outline" size="16" />
            Saved on this device only — couldn't sync to your account
          </span>
        </v-card-actions>
      </template>

      <!-- Action Needed tab content -->
      <template v-if="topTab === 'actionNeeded'">
        <v-card-text class="settings-dialog__content settings-dialog__content--tags">
          <ActionNeededManagement :scope="store.scope" />
        </v-card-text>
      </template>

      <!-- Tags tab content -->
      <template v-if="topTab === 'tags'">
        <v-card-text class="settings-dialog__content settings-dialog__content--tags settings-dialog__content--fill">
          <TagManagement />
        </v-card-text>
      </template>

      <!-- Substatus Tags tab content -->
      <template v-if="topTab === 'substatusTags'">
        <v-card-text class="settings-dialog__content settings-dialog__content--tags">
          <SubstatusTagManagement />
        </v-card-text>
      </template>

      <!-- Groups tab content -->
      <template v-if="topTab === 'groups'">
        <v-card-text class="settings-dialog__content settings-dialog__content--tags settings-dialog__content--fill">
          <GroupManagement />
        </v-card-text>
      </template>

      <!-- Status Notifications tab content -->
      <template v-if="topTab === 'statusNotifications'">
        <v-card-text class="settings-dialog__content settings-dialog__content--tags">
          <StatusNotificationManagement :scope="store.scope" />
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { titleCaseString } from "@/utils";
import { useRequirementsStore, useTableSettingsStore } from "@/stores";
import { PA_COLUMNS, INTAKE_COLUMNS, COLUMN_TABS } from "@/constants/tableColumns";
import { WORKLIST_COLUMNS } from "@/constants/worklistColumns";
import { worklistShownFor } from "@/constants/worklistSections";
import { SCOPE, CLIENT_CONFIG_SECTION } from "@/constants/scope";
import ActionNeededManagement from "@/components/dashboard/ActionNeededManagement.vue";
import TagManagement from "@/components/dashboard/TagManagement.vue";
import SubstatusTagManagement from "@/components/dashboard/SubstatusTagManagement.vue";
import GroupManagement from "@/components/dashboard/GroupManagement.vue";
import StatusNotificationManagement from "@/components/dashboard/StatusNotificationManagement.vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});
defineEmits(["update:modelValue"]);

const store = useTableSettingsStore();
const requirementsStore = useRequirementsStore();
const topTab = ref("columns");
const dragIndex = ref(null);
const dropTargetIndex = ref(null);

const DEFAULTS_FOR = { [SCOPE.PA]: PA_COLUMNS, [SCOPE.INTAKE]: INTAKE_COLUMNS, [SCOPE.WORKLIST]: WORKLIST_COLUMNS };
const currentDefaults = computed(() => DEFAULTS_FOR[store.scope]);
const currentPrefs = computed(
  () =>
    ({
      [SCOPE.PA]: store.paColumnPrefs,
      [SCOPE.INTAKE]: store.intakeColumnPrefs,
      [SCOPE.WORKLIST]: store.worklistColumnPrefs,
    }[store.scope])
);
const worklistShown = computed(() =>
  worklistShownFor(requirementsStore.worklistEnabled, requirementsStore.visibleModules)
);

watch(
  () => props.modelValue && store.scope === SCOPE.WORKLIST && !worklistShown.value,
  (unavailable) => {
    if (unavailable) setScope(SCOPE.PA);
  },
  { immediate: true }
);

// The Substatus column exists only for tenants that defined substatus tags (Settings → Substatus Tags);
// the grid hides it otherwise, so its toggle is hidden here too rather than offering a no-op switch.
const substatusConfigured = computed(() =>
  (store.scope === SCOPE.WORKLIST ? [SCOPE.PA, SCOPE.INTAKE] : [store.scope]).some(
    (scope) => (requirementsStore.clientConfig?.[CLIENT_CONFIG_SECTION[scope]]?.substatusTags || []).length > 0
  )
);

const currentItems = computed(() => {
  const defaults = currentDefaults.value;
  // Every pref stays in the list so drag indexes line up with the rendered rows and a reorder never
  // demotes a column the user cannot see; the unconfigured Substatus row is merely not shown.
  return [...currentPrefs.value]
    .sort((a, b) => a.order - b.order)
    .map((pref) => {
      const def = defaults.find((c) => c.key === pref.key);
      return {
        key: pref.key,
        label: def?.label || pref.key,
        visible: pref.visible,
        pinned: !!def?.pinned,
        hidden: pref.key === "substatusTags" && !substatusConfigured.value,
      };
    });
});

watch(
  () => store.scope,
  (scope) => {
    if (!COLUMN_TABS[scope].includes(topTab.value)) topTab.value = COLUMN_TABS[scope][0];
  }
);

function setScope(scope) {
  store.setScope(scope);
  if (!COLUMN_TABS[scope].includes(topTab.value)) {
    topTab.value = COLUMN_TABS[scope][0];
  }
}

function onToggle(colKey, visible) {
  store.setColumnVisibility(store.scope, colKey, visible);
}

function onReset() {
  store.resetColumns(store.scope);
}

function onDragStart(event, index) {
  if (currentItems.value[index].pinned) {
    event.preventDefault();
    return;
  }
  dragIndex.value = index;
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData("text/plain", String(index));
}

function onDragOver(event, index) {
  if (dragIndex.value === null || dragIndex.value === index) return;
  if (currentItems.value[index].pinned) return;
  dropTargetIndex.value = index;
}

function onDragLeave(index) {
  if (dropTargetIndex.value === index) {
    dropTargetIndex.value = null;
  }
}

function onDrop(event, toIndex) {
  event.preventDefault();
  const fromIndex = dragIndex.value;
  if (fromIndex === null || fromIndex === toIndex) return;
  if (currentItems.value[toIndex].pinned) return;

  const items = [...currentItems.value];
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);

  const orderedKeys = items.map((i) => i.key);
  store.setColumnOrder(store.scope, orderedKeys);
  dragIndex.value = null;
  dropTargetIndex.value = null;
}

function onDragEnd() {
  dragIndex.value = null;
  dropTargetIndex.value = null;
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.settings-dialog {
  border-radius: 12px !important;
}

.settings-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: $lh_font_family_dm_sans;
  font-size: $lh_h4_size;
  font-weight: $lh_font_weight_bold;
  color: $lh_color_navy_dark;
  padding: 20px 24px 12px;
}

.settings-dialog__title-text {
  flex-shrink: 0;
}

.settings-dialog__tabs {
  padding: 0 24px;
}

.settings-dialog__content {
  padding: 16px 24px !important;
  max-height: 420px;
  overflow-y: auto;
}

.settings-dialog__content--tags {
  padding: 12px 24px 8px !important;
  max-height: 70vh;
  overflow-y: auto;
}

.settings-dialog__content--fill {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.settings-dialog__toggle-row {
  margin-bottom: 14px;
}

.settings-dialog__pill-toggle {
  display: inline-flex;
  background: $lh_color_snowy_white;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.settings-dialog__pill {
  font-family: $lh_font_family_dm_sans;
  font-size: 13px;
  font-weight: 500;
  color: $lh_color_blue_gray;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 5px 14px;
  cursor: pointer;
  transition: all $lh_transition_fast;

  &:hover:not(.settings-dialog__pill--active) {
    color: $lh_color_navy_dark;
  }
}

.settings-dialog__pill--active {
  background: white;
  color: $lh_color_navy_dark;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.settings-dialog__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.settings-dialog__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background-color $lh_transition_fast, opacity $lh_transition_fast;
  user-select: none;

  &:hover {
    background: $lh_color_snowy_white;
  }
}

.settings-dialog__row--dragging {
  opacity: 0.4;
}

.settings-dialog__row--drop-target {
  border-top: 2px solid $lh_color_blue;
}

.settings-dialog__row--pinned {
  cursor: default;
}

.settings-dialog__drag-handle {
  color: $lh_color_blue_gray;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.settings-dialog__drag-handle--disabled {
  opacity: 0.3;
  cursor: default;
}

.settings-dialog__label {
  flex: 1;
  font-family: $lh_font_family_dm_sans;
  font-size: $lh_body3_1_size;
  color: $lh_color_navy_dark;
}

.settings-dialog__chip {
  font-size: 10px;
}

.settings-dialog__switch {
  flex: 0 0 auto;
}

.settings-dialog__actions {
  padding: 8px 24px 16px;
  justify-content: flex-start;
}

.settings-dialog__loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 4px;
  font-size: 13px;
  opacity: 0.7;
}

.settings-dialog__save-error {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgb(var(--v-theme-warning));
}
</style>
